import { useCallback } from 'react';
import { logEvent } from '@eventbrite/legacy-infra.statsig';
import { usePicoXTrack } from '@eventbrite/bsp-sdk/react';
import { GACodes, GetTicketsClickType, PICO_EVENT_NAMES, STATSIG_EVENT_NAMES } from '@/components/_shared/constants';
import { trackAnalyticsEvent } from '@eventbrite/legacy-infra.site-analytics';
import { toPrivateEventId } from '@/utils/picoEventId';

type Args = {
  eventId?: string;
  // Series parent id, used as `parent_eb_event_id` for timed-entry events.
  seriesId?: string;
  // From `ticketInfo.availability.hasEarlyBirdTickets`. `null` when ticket
  // info hasn't loaded yet (rare — the conversion-bar CTA only enables once
  // ticketInfo resolves, but the click can still race the response).
  hasEarlyBirdTickets?: boolean | null;
};

// Maps the CTA's translation id (passed by `CheckoutButton` via
// `buttonLabel.id`) to the Pico `type` value. Returns `undefined` for
// CTAs that fall outside the four documented values (`Details` for
// SalesStartSoon "Remind me", `Yes` for RSVP confirm) — those clicks
// should not fire `get_tickets_click`.
const PICO_TYPE_BY_LABEL: Record<string, GetTicketsClickType> = {
  'Get tickets': 'purchase',
  'Check availability': 'timed_entry',
  'Join Waitlist': 'waitlist',
  'Reserve a spot': 'reserve',
};

export const useTrackCheckoutButtonClick = ({ eventId, seriesId, hasEarlyBirdTickets }: Args = {}) => {
  const picoTrack = usePicoXTrack();

  const trackCheckoutButtonClick = useCallback(
    (trackingLabel?: string) => {
      logEvent(STATSIG_EVENT_NAMES.LISTING_CONVERSION_BAR_CTA_CLICK);
      trackAnalyticsEvent({
        action: GACodes.GA_ACTION_CHECKOUT,
        category: GACodes.GA_CATEGORY_LISTING,
        label: trackingLabel ?? 'Get Tickets',
      });

      const type = trackingLabel ? PICO_TYPE_BY_LABEL[trackingLabel] : undefined;
      if (type) {
        picoTrack(PICO_EVENT_NAMES.GET_TICKETS_CLICK, {
          eb_event_id: toPrivateEventId(eventId),
          parent_eb_event_id: toPrivateEventId(seriesId),
          early_bird_discount_present: hasEarlyBirdTickets ?? null,
          type,
        });
      }
    },
    [picoTrack, eventId, seriesId, hasEarlyBirdTickets],
  );

  return trackCheckoutButtonClick;
};
